"use client";
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  checkAuthStatus, 
  refreshAdminToken, 
  logoutAdmin,
  getCurrentAdmin,
  verifyAdminLoginCode
} from '@/utils/adminSignInApi';

interface AdminUserData {
  id: string;
  email: string;
  username: string;
  role: string;
  isActive: boolean;
  lastLoginAt?: Date;
  createdAt?: Date;
}

interface AdminAuthContextType {
  isAuthenticated: boolean;
  user: AdminUserData | null;
  loading: boolean;
  checkAuthStatus: () => Promise<boolean>;
  logoutUser: () => Promise<void>;
  login: (email: string, code: string) => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<AdminUserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const checkAuth = async (): Promise<boolean> => {
    try {
      setLoading(true);
      
      // Check if we have user info in localStorage
      const storedUserInfo = localStorage.getItem('admin_user_info');
      let userData = null;
      
      if (storedUserInfo) {
        userData = JSON.parse(storedUserInfo);
      }
      
      // Verify with the server
      const response = await checkAuthStatus();
      
      if (response.authenticated) {
        setIsAuthenticated(true);
        
        // Get full user data from /me endpoint
        try {
          const fullUserResponse = await getCurrentAdmin();
          setUser(fullUserResponse.admin);
          
          // Update localStorage with latest user data
          localStorage.setItem('admin_user_info', JSON.stringify(fullUserResponse.admin));
        } catch (error) {
          // Fall back to basic user data from status endpoint or localStorage
          setUser(response.admin || userData);
          if (response.admin) {
            localStorage.setItem('admin_user_info', JSON.stringify(response.admin));
          }
        }
        
        return true;
      } else {
        // Try refreshing the token
        try {
          const refreshResponse = await refreshAdminToken();
          
          if (refreshResponse.success) {
            setIsAuthenticated(true);
            setUser(refreshResponse.admin || userData);
            
            // Update localStorage with latest user data
            if (refreshResponse.admin) {
              localStorage.setItem('admin_user_info', JSON.stringify(refreshResponse.admin));
            }
            
            return true;
          } else {
            // Clear auth state
            setIsAuthenticated(false);
            setUser(null);
            localStorage.removeItem('admin_user_info');
            return false;
          }
        } catch (refreshError) {
          // Clear auth state
          setIsAuthenticated(false);
          setUser(null);
          localStorage.removeItem('admin_user_info');
          return false;
        }
      }
    } catch (error) {
      console.error('Error checking admin authentication status:', error);
      setIsAuthenticated(false);
      setUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logoutUser = async (): Promise<void> => {
    try {
      // Call logout API
      await logoutAdmin();
      
      // Clear auth state
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('admin_user_info');
    } catch (error) {
      console.error('Error during admin logout:', error);
      // Still clear local state even if API fails
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('admin_user_info');
    }
  };

  const login = async (email: string, code: string): Promise<void> => {
    try {
      setLoading(true);
      
      // Verify login code
      const response = await verifyAdminLoginCode(email, code);
      
      if (response.success) {
        setIsAuthenticated(true);
        setUser(response.admin);
        
        // Store user data in localStorage
        localStorage.setItem('admin_user_info', JSON.stringify(response.admin));
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error; // Re-throw so the component can handle it
    } finally {
      setLoading(false);
    }
  };

  // Check auth on initial load
  useEffect(() => {
    checkAuth();
    
    // Set up a timer to refresh the token periodically (every 10 minutes)
    const refreshInterval = setInterval(() => {
      if (isAuthenticated) {
        refreshAdminToken()
          .then(response => {
            if (!response.success) {
              // If refresh fails, check auth status fully
              checkAuth();
            } else {
              // Update user data if refresh provides it
              if (response.admin) {
                setUser(response.admin);
                localStorage.setItem('admin_user_info', JSON.stringify(response.admin));
              }
            }
          })
          .catch(() => {
            checkAuth();
          });
      }
    }, 10 * 60 * 1000); // 10 minutes
    
    return () => clearInterval(refreshInterval);
  }, [isAuthenticated]);

  return (
    <AdminAuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      loading, 
      checkAuthStatus: checkAuth, 
      logoutUser,
      login
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};